import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { getEspecialidades, listarPacientes } from "../../../../helpers/api";
import { Transfer, Select } from "antd";
import axios from "axios";
import FancySelect from "components/FancySelect";

interface CreateReavaliacaoModalProps {
  onHide: () => void;
  show: boolean;
  view: any[];
  instrumentos: any[];
}

interface Paciente {
  id: number;
  apelido: string;
  unidade_id: number;
}

interface Colaborador {
  id: number;
  nome: string;
  aplicador: boolean;
  apelido: string;
}

interface InstrumentoComChave {
  key: string;
  id: number;
  instrumento: string;
}

interface InstrumentoComAplicadores {
  instrumentoId: number;
  instrumento: string;
  aplicadoresIds: number[];
}

async function criarReavaliacao(
  pacienteId: number,
  avaliacaoReavaliacao: string,
  instrumentosComAplicadores: InstrumentoComAplicadores[],
  unidadeId: number,
  especialidades: number[],
) {
  const associacoes = instrumentosComAplicadores.flatMap((i) => {
      return i.aplicadoresIds.map((aplicadorId) => ({
        instrumento_id: i.instrumentoId,
        colaborador_id: aplicadorId,
      }))
  }
  );
    await axios.post(
    `/relatorio-parcial/paciente/${pacienteId}`,
    {
      unidade_id: unidadeId,
      especialidades: especialidades,
      publicado: false,
      referente_ao_periodo: `Um ano retroativo desde ${avaliacaoReavaliacao}`,
      periodicidade_em_dias: 365,
      avaliacao_reavaliacao: `${avaliacaoReavaliacao} 00:00:00.000 +00:00`,
      associacoes: associacoes
    },
  );
}

export default function CreateReavaliacaoModal({
  onHide,
  show,
  view,
  instrumentos,
}: CreateReavaliacaoModalProps) {
  const [erro, setErro] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [pacienteId, setPacienteId] = useState<any>(null);
  const [formData, setFormData] = useState<string>("");
  const [instrumentoKey, setInstrumentoKey] = useState<string[]>([]);
  const [especialidadesSelecionadas, setEspecialidadesSelecionadas] = useState<
    number[]
  >([]);
  const [especialidades, setEspecialidades] = useState<
    {
      id: number;
      descricao: string;
    }[]
  >([]);
  const [aplicadoresPorInstrumento, setAplicadoresPorInstrumento] = useState<
    Record<number, number[]>
  >({});

  const instrumentosComChave: InstrumentoComChave[] = instrumentos.map((i) => ({
    key: String(i.id),
    id: i.id,
    instrumento: i.instrumento,
  }));

  useEffect(() => {
    const fetchEspecialidades = async () => {
      const { data } = await getEspecialidades("?pageSize=100");
      setEspecialidades(data.result);
    };
    fetchEspecialidades();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pacientesData = await listarPacientes("?pageSize=1000000");
        setPacientes(pacientesData.data.result || []);

        const colaboradoresResponse = await axios.get(
          "/colaborador?pageSize=1000000",
        );
        setColaboradores(colaboradoresResponse.data.result || []);
      } catch (err) {
        setErro("Erro ao carregar pacientes e colaboradores");
      }
    };
    if (show) {
      fetchData();
    }
  }, [show]);

  const getInstrumentosValidos = () => {
    return instrumentosComChave.filter((i) => instrumentoKey.includes(i.key));
  };

  const getInstrumentosComAplicadores = (): InstrumentoComAplicadores[] => {
    return getInstrumentosValidos()
      .map((i) => ({
        instrumentoId: i.id,
        instrumento: i.instrumento,
        aplicadoresIds: aplicadoresPorInstrumento[i.id] || [],
      }))
      .filter((i) => i.aplicadoresIds.length > 0);
  };

  const obterDataProximaAvaliacao = (pacienteIdCheck: number): string => {
    const avaliacao = view.find((p) => p.paciente_id === pacienteIdCheck);
    if (!avaliacao) return "";
    if (!avaliacao?.avaliacao_reavaliacao) {
      return "";
    }

    const datahora = avaliacao.avaliacao_reavaliacao;
    const data = datahora.slice(0, 10);
    const ano = data.slice(0, 4);
    const diames = data.slice(5);
    return `${Number(ano) + 1}-${diames}`;
  };

  const handleChangePaciente = (selectedPaciente: any) => {
    setPacienteId(selectedPaciente);
    setErro(undefined);
    const dataProxima = obterDataProximaAvaliacao(selectedPaciente.value);
    setFormData(dataProxima || "");
  };

  const handleInstrumentosChange = (keys: string[]) => {
    setInstrumentoKey(keys);
    const novoMapeamento = { ...aplicadoresPorInstrumento };
    instrumentosComChave.forEach((item) => {
      const id = item.id;
      if (!keys.includes(item.key)) {
        delete novoMapeamento[id];
      } else if (!novoMapeamento[id]) {
        novoMapeamento[id] = [];
      }
    });
    setAplicadoresPorInstrumento(novoMapeamento);
  };

  const handleAplicadoresChange = (
    instrumentoId: number,
    aplicadorIds: number[],
  ) => {
    setAplicadoresPorInstrumento((prev) => ({
      ...prev,
      [instrumentoId]: aplicadorIds,
    }));
  };
  const temTodosAplicadoresPreenchidos = (): boolean => {
    return getInstrumentosValidos().every((i) => {
      const aplicadores = aplicadoresPorInstrumento[i.id];
      return aplicadores && aplicadores.length > 0;
    });
  };

  const handleSalvar = async () => {
    if (!pacienteId || !formData || instrumentoKey.length === 0) {
      setErro("Por favor, preencha todos os campos");
      return;
    }

    if (!temTodosAplicadoresPreenchidos()) {
      setErro(
        "Por favor, selecione pelo menos um aplicador para cada instrumento",
      );
      return;
    }

    setSaving(true);
    setErro(undefined);

    try {
      const paciente = pacientes.find((p) => p.id === pacienteId.value);
      await criarReavaliacao(
        pacienteId.value,
        formData,
        getInstrumentosComAplicadores(),
        paciente?.unidade_id!,
        especialidadesSelecionadas,
      );

      setPacienteId(null);
      setFormData("");
      setInstrumentoKey([]);
      setAplicadoresPorInstrumento({});
      onHide();
    } catch (err: any) {
      const mensagemErro =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Erro ao criar a reavaliação";
      setErro(mensagemErro);
    } finally {
      setSaving(false);
    }
  };

  const handleFechar = () => {
    setPacienteId(null);
    setFormData("");
    setInstrumentoKey([]);
    setAplicadoresPorInstrumento({});
    setErro(undefined);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleFechar} centered size="lg">
      <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Modal.Title
          style={{ fontSize: "1.1rem", fontWeight: 600, color: "#2c3e35" }}
        >
          Nova Avaliação / Reavaliação
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "24px" }}>
        <Form>
          {/* Seleção de Paciente */}
          <Form.Group style={{ marginBottom: "20px" }}>
            <Form.Label
              style={{ fontWeight: 600, color: "#2c3e35", marginBottom: "8px" }}
            >
              Paciente
            </Form.Label>
            <div>
              {!erro ? (
                <FancySelect
                  label=""
                  value={pacienteId}
                  name="apelido"
                  placeholder="Selecione um paciente"
                  options={pacientes.map((p) => ({
                    value: p.id,
                    label: p.apelido,
                  }))}
                  onChange={handleChangePaciente}
                />
              ) : (
                <div
                  style={{
                    padding: "12px",
                    backgroundColor: "#fef5ec",
                    borderRadius: "6px",
                    color: "#c8960a",
                    fontSize: "0.875rem",
                  }}
                >
                  <i className="mdi mdi-alert-circle me-2" />
                  Erro ao carregar pacientes
                </div>
              )}
            </div>
          </Form.Group>
          {pacienteId && (
            <>
              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#2c3e35",
                    marginBottom: "8px",
                  }}
                >
                  Data da Avaliação
                </Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData}
                  onChange={(e) => {
                    setFormData(e.currentTarget.value);
                    setErro(undefined);
                  }}
                  style={{ borderRadius: "6px", borderColor: "#ddd" }}
                />
                <small
                  style={{ color: "#999", marginTop: "4px", display: "block" }}
                >
                  Selecione a data da avaliação ou reavaliação
                </small>
              </Form.Group>
              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#2c3e35",
                    marginBottom: "8px",
                  }}
                >
                  Especialidades
                </Form.Label>
                <Select
                  style={{ width: "100%" }}
                  optionFilterProp="label"
                  options={especialidades.map((e) => {
                    return {
                      value: e.id,
                      label: e.descricao,
                    };
                  })}
                  onChange={(e) => {
                    setEspecialidadesSelecionadas(e);
                  }}
                  dropdownStyle={{ zIndex: 9999 }}
                  mode="multiple"
                  placeholder="Selecione uma ou mais especialidades"
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: "20px" }}>
                <Form.Label
                  style={{
                    fontWeight: 600,
                    color: "#2c3e35",
                    marginBottom: "8px",
                  }}
                >
                  Instrumentos
                </Form.Label>
                <div
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    padding: "12px",
                    border: "1px solid #ebebeb",
                  }}
                >
                  <Transfer
                    targetKeys={instrumentoKey}
                    onChange={handleInstrumentosChange}
                    dataSource={instrumentosComChave}
                    render={(item) => item.instrumento}
                    listStyle={{
                      width: "100%",
                      height: "250px",
                    }}
                  />
                </div>
                <small
                  style={{ color: "#999", marginTop: "8px", display: "block" }}
                >
                  Mova os instrumentos desejados para a coluna da direita
                </small>
              </Form.Group>

              {/* Seleção de Aplicadores por Instrumento */}
              {getInstrumentosValidos().length > 0 && (
                <Form.Group style={{ marginBottom: "20px" }}>
                  <Form.Label
                    style={{
                      fontWeight: 600,
                      color: "#2c3e35",
                      marginBottom: "12px",
                    }}
                  >
                    Aplicadores por Instrumento
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "16px",
                    }}
                  >
                    {getInstrumentosValidos().map((instrumento) => (
                      <div
                        key={instrumento.id}
                        style={{
                          backgroundColor: "#f8f9fa",
                          borderRadius: "6px",
                          padding: "12px",
                          border: "1px solid #ebebeb",
                        }}
                      >
                        <div style={{ marginBottom: "8px" }}>
                          <span
                            style={{
                              fontWeight: 500,
                              color: "#2c3e35",
                              fontSize: "0.95rem",
                            }}
                          >
                            {instrumento.instrumento}
                          </span>
                        </div>
                        <Select
                          optionFilterProp="label"
                          dropdownStyle={{ zIndex: 9999 }}
                          mode="multiple"
                          placeholder="Selecione um ou mais aplicadores"
                          value={aplicadoresPorInstrumento[instrumento.id]}
                          onChange={(value) =>
                            handleAplicadoresChange(instrumento.id, value)
                          }
                          style={{ width: "100%" }}
                          options={colaboradores
                            .filter((c) => c.aplicador)
                            .map((c) => ({
                              label: c.apelido,
                              value: String(c.id),
                            }))}
                        />
                      </div>
                    ))}
                  </div>
                  <small
                    style={{
                      color: "#999",
                      marginTop: "8px",
                      display: "block",
                    }}
                  >
                    Selecione pelo menos um aplicador para cada instrumento
                  </small>
                </Form.Group>
              )}
            </>
          )}

          {erro && (
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#fef5ec",
                borderRadius: "6px",
                color: "#c8960a",
                border: "1px solid #f5dda0",
                marginBottom: "12px",
                fontSize: "0.875rem",
              }}
            >
              <i className="mdi mdi-alert-circle me-2" />
              {erro}
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: "1px solid #f0f0f0",
          padding: "16px 24px",
          gap: "8px",
        }}
      >
        <Button
          variant="light"
          onClick={handleFechar}
          disabled={saving}
          style={{ borderRadius: "6px", color: "#888" }}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSalvar}
          disabled={
            especialidadesSelecionadas.length === 0 ||
            !pacienteId ||
            !formData ||
            instrumentoKey.length === 0 ||
            !temTodosAplicadoresPreenchidos() ||
            saving
          }
          style={{ borderRadius: "6px" }}
        >
          {saving ? (
            <>
              <i className="mdi mdi-loading mdi-spin me-2" />
              Salvando...
            </>
          ) : (
            <>
              <i className="mdi mdi-check me-2" />
              Salvar
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
